import { useState } from 'react';

import type { EUROPACE_COMPLETENESS_REASONS, EUROPACE_INCOMPLETENESS_MESSAGES_IDENTIFIER_TYPES } from 'cpool-constants';
import type { ValueOf } from 'cpool-utils';
import { useTranslation } from 'react-i18next';

import { useOffersByRequestId } from '~/features/leadDetails/hooks/useOffersByRequestId';

type Props = {
    leadId: string;
    currentRequestId: string;
};

export const BankOfferWarnings: React.FC<Props> = ({ currentRequestId, leadId }) => {
    const { t } = useTranslation();
    const { data: offerRequestArray } = useOffersByRequestId({ id: leadId, requestId: currentRequestId });
    const [showAllWarnings, setShowAllWarnings] = useState(false);

    // collect all incompletenessMessages from all responses for this requestId (responses of all banks - europace, consors, etc.)
    const warnings = offerRequestArray?.reduce((acc, curr) => {
        return { ...acc, ...curr.incompletenessMessages };
    }, {} as Record<string, number>);

    if (warnings === undefined || Object.keys(warnings).length === 0) return null;

    // sort warnings by number of occurances
    const sortedWarnings = Object.entries(warnings).sort((a, b) => b[1] - a[1]);

    const translatedWarnings = sortedWarnings.map((warning) => {
        // consoder adjusting the function when new banks are added
        const message = warning[0];
        const count = warning[1];
        const messageElements = message.split(':');
        const type = messageElements[0] as ValueOf<typeof EUROPACE_INCOMPLETENESS_MESSAGES_IDENTIFIER_TYPES>; // constant currently taken from Europace
        const propertySegment = messageElements[1];
        const property = propertySegment
            ? propertySegment.charAt(0) + propertySegment.slice(1).toLowerCase().replace(/\./g, '-')
            : '';
        const reason = messageElements[2] as ValueOf<typeof EUROPACE_COMPLETENESS_REASONS>; // constant currently taken from Europace
        const convertedMessage = `${property} ${t(`BANK_CREDIT_REQUEST.OFFER_RESPONSE_WARNINGS.TYPE.${type}`)} ${t(
            `BANK_CREDIT_REQUEST.OFFER_RESPONSE_WARNINGS.REASON.${reason}`,
        )}`;
        return [convertedMessage, count] as const;
    });

    let displyReadyWarnings = translatedWarnings.slice(0, 5);
    let footer: React.JSX.Element | null = (
        <>
            <span className="text-xs">{t('BANK_CREDIT_REQUEST.MORE_WARNINGS_EXIST')} </span>
            <button type="button" className="text-blue underline text-xs" onClick={() => setShowAllWarnings(true)}>
                {t('BANK_CREDIT_REQUEST.SHOW_ALL_WARNINGS')}
            </button>
        </>
    );

    if (showAllWarnings) {
        displyReadyWarnings = translatedWarnings;
        footer = (
            <button type="button" className="text-blue underline text-xs" onClick={() => setShowAllWarnings(false)}>
                {t('BANK_CREDIT_REQUEST.SHOW_SELECTED_WARNINGS')}
            </button>
        );
    }

    if (translatedWarnings.length <= 5) footer = null;

    return (
        <>
            <div className="mt-2">{t('BANK_CREDIT_REQUEST.WARNING_MISSING_DATA')}</div>
            <ul className="list-disc pl-5">
                {displyReadyWarnings.map((warning) => (
                    <li key={warning[0]}>
                        {warning[0]} ({warning[1]}{' '}
                        {t('BANK_CREDIT_REQUEST.BANKS_MISSING_THIS_INFO', { count: warning[1] })})
                    </li>
                ))}
            </ul>
            {footer}
        </>
    );
};
